import React, { useState } from "react";
import { KIcon } from "../../../../../shared/icon/k-icon";
import KuikaRadioGroup, { IKuikaRadioOption } from "../../../../ui-kit/kuika-radio-group/kuika-radio-group";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

const OPTIONS: IKuikaRadioOption[] = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" }
];

const CARD_OPTIONS: IKuikaRadioOption[] = [
  {
    label: "Native",
    value: "native",
    description: "Android (Kotlin) + iOS (Swift), separate projects",
    icon: <KIcon iconLibrary="fontawesome" iconType="solid" iconName="mobile-screen-button" />
  },
  {
    label: "React Native",
    value: "reactNative",
    description: "One Expo project for both platforms",
    badge: "Beta",
    badgeTone: "warning",
    icon: <KIcon iconLibrary="fontawesome" iconType="brands" iconName="react" />
  }
];

interface IStatefulProps {
  initial?: string;
  align?: "center" | "top";
  labelPosition?: "right" | "left";
  disabled?: boolean;
}

const Stateful: React.FC<IStatefulProps> = ({ initial = "weekly", ...rest }) => {
  const [value, setValue] = useState<string | number>(initial);
  return <KuikaRadioGroup options={OPTIONS} value={value} onChange={setValue} {...rest} />;
};

interface IStatefulCardsProps {
  initial?: string;
  disabled?: boolean;
  columns?: number;
}

const StatefulCards: React.FC<IStatefulCardsProps> = ({ initial = "native", ...rest }) => {
  const [value, setValue] = useState<string | number>(initial);
  return <KuikaRadioGroup variant="card" options={CARD_OPTIONS} value={value} onChange={setValue} {...rest} />;
};

export const RadioGroupStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Radio Group"
      componentName="KuikaRadioGroup"
      sourcePath="ui-kit/kuika-radio-group"
      description="Single-select cluster with label position and vertical alignment options, plus a card variant for choices whose consequence needs naming. One dot slides from ring to ring as the pick changes (at once on first render and under reduced motion); arrow keys move the pick. Backed by Radix primitives."
    >
      <SpecRow label="Label position">
        <Stateful labelPosition="right" />
        <Stateful labelPosition="left" />
      </SpecRow>
      <SpecRow label="Vertical alignment" align="start">
        <Stateful align="center" />
        <Stateful align="top" />
      </SpecRow>
      <SpecRow label="Disabled">
        <Stateful disabled />
      </SpecRow>
      <SpecRow label="Card variant" caption="icon, label, one-line consequence and an optional maturity badge">
        <div style={{ maxWidth: 520, width: "100%" }}>
          <StatefulCards />
        </div>
      </SpecRow>
      <SpecRow label="Card variant · disabled">
        <div style={{ maxWidth: 520, width: "100%" }}>
          <StatefulCards disabled />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
